import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export const appStyles = {
  page: "space-y-6",
  card: "rounded-xl border border-[#E6E0D6] bg-white shadow-sm",
  mutedCard: "rounded-xl border border-[#E6E0D6] bg-[#FBFAF7] shadow-sm",
  tableWrap: "overflow-hidden rounded-xl border border-[#E6E0D6] bg-white shadow-sm",
  table: "w-full min-w-[720px] border-collapse text-left text-sm",
  tableHead: "bg-[#F3EFE9] text-xs font-semibold uppercase tracking-[0.08em] text-[#5C625F]",
  tableCell: "border-t border-[#E6E0D6] px-4 py-3 align-middle text-[#2A4B46]",
  field: "h-11 rounded-lg border border-[#D8D1C7] bg-white px-3 text-sm text-[#2A4B46] outline-none transition focus:border-[#2A4B46] focus:ring-2 focus:ring-[#2A4B46]/15",
  textarea: "min-h-28 rounded-lg border border-[#D8D1C7] bg-white px-3 py-2 text-sm text-[#2A4B46] outline-none transition focus:border-[#2A4B46] focus:ring-2 focus:ring-[#2A4B46]/15",
  label: "text-sm font-semibold text-[#2A4B46]",
  hint: "text-xs leading-5 text-[#6E746F]",
  buttonPrimary: "inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#2A4B46] px-4 text-sm font-semibold text-white transition hover:bg-[#203A36] disabled:cursor-not-allowed disabled:opacity-60",
  buttonSecondary: "inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-[#D8D1C7] bg-white px-4 text-sm font-semibold text-[#2A4B46] transition hover:bg-[#F3EFE9] disabled:cursor-not-allowed disabled:opacity-60",
  buttonDanger: "inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60",
};

export const badgeTones = {
  neutral: "bg-[#F3EFE9] text-[#2A4B46] border-[#E6E0D6]",
  success: "bg-emerald-50 text-emerald-700 border-emerald-100",
  warning: "bg-amber-50 text-amber-700 border-amber-100",
  danger: "bg-red-50 text-red-700 border-red-100",
  info: "bg-sky-50 text-sky-700 border-sky-100",
  gold: "bg-[#F7E9D8] text-[#A86F3C] border-[#ECD1B2]",
};

export function PageShell({ children, className = "", ...props }) {
  return (
    <div className={cn(appStyles.page, className)} {...props}>
      {children}
    </div>
  );
}

export function PageHeader({ title, description, actions, eyebrow, className = "" }) {
  return (
    <div className={cn("flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between", className)}>
      <div className="min-w-0">
        {eyebrow && <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#D4A373]">{eyebrow}</p>}
        <h1 className="text-xl font-bold leading-tight text-[#2A4B46] font-['Outfit'] sm:text-2xl">
          {title}
        </h1>
        {description && <p className="mt-2 max-w-3xl text-sm leading-6 text-[#5C625F] sm:text-base">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2 sm:justify-end">{actions}</div>}
    </div>
  );
}

export function CardGrid({ children, className = "" }) {
  return <div className={cn("grid gap-4 sm:grid-cols-2 xl:grid-cols-4", className)}>{children}</div>;
}

export function StatCard({ title, value, icon: Icon, tone = "gold", testId }) {
  const toneClass = {
    pine: "bg-[#E7EFEC] text-[#2A4B46]",
    gold: "bg-[#F7E9D8] text-[#A86F3C]",
    blue: "bg-blue-50 text-blue-800",
    green: "bg-green-50 text-green-800",
    red: "bg-red-50 text-red-800",
  }[tone] || "bg-[#F3EFE9] text-[#2A4B46]";

  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-[#5C625F]">{title}</CardTitle>
        {Icon && (
          <div className={cn("flex h-9 w-9 items-center justify-center rounded-full", toneClass)}>
            <Icon size={17} />
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-[#2A4B46] sm:text-3xl" data-testid={testId}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

export function SectionCard({ title, children, className = "", contentClassName = "" }) {
  return (
    <Card className={cn(appStyles.card, className)}>
      {title && (
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#2A4B46] font-['Outfit']">{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent className={contentClassName}>{children}</CardContent>
    </Card>
  );
}

export function QuickAction({ href, icon: Icon, title, description, testId }) {
  return (
    <a
      href={href}
      className="flex min-h-16 items-center gap-3 rounded-xl bg-[#F3EFE9] p-4 transition-colors hover:bg-[#E6E4E0]"
      data-testid={testId}
    >
      {Icon && <Icon size={18} className="shrink-0 text-[#2A4B46]" />}
      <span className="min-w-0">
        <p className="font-semibold text-[#2A4B46]">{title}</p>
        {description && <p className="text-sm text-[#5C625F]">{description}</p>}
      </span>
    </a>
  );
}

export function LoadingState({ children, label = "Chargement..." }) {
  return <div className="py-12 text-center text-sm text-[#5C625F]">{children || label}</div>;
}

export function StatusBadge({ children, tone = "neutral", className = "" }) {
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold", badgeTones[tone] || badgeTones.neutral, className)}>
      {children}
    </span>
  );
}

export function EmptyState({ icon: Icon, title = "Aucune donnee", description, action, className = "" }) {
  return (
    <div className={cn("flex flex-col items-center justify-center rounded-xl border border-dashed border-[#D8D1C7] bg-[#FBFAF7] px-4 py-10 text-center", className)}>
      {Icon && (
        <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-[#F3EFE9] text-[#2A4B46]">
          <Icon size={20} />
        </div>
      )}
      <h3 className="text-base font-semibold text-[#2A4B46]">{title}</h3>
      {description && <p className="mt-1 max-w-md text-sm leading-6 text-[#5C625F]">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function DataTableShell({ children, className = "" }) {
  return <div className={cn(appStyles.tableWrap, className)}>{children}</div>;
}
